import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { api } from '../services/api';

interface AuditLog {
  id: number;
  user_id: number;
  action: string;
  description: string;
  target_sale_id?: number;
  target_customer_id?: number;
  old_value?: string;
  new_value?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  user?: {
    id: number;
    username: string;
    role: string;
  };
  sale?: {
    id: number;
    customer: {
      first_name: string;
      last_name: string;
    };
  };
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    action: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    limit: 100
  });

  const actionTypes = [
    { value: 'cobranzas', label: 'Cobranzas' },
    { value: 'modificacion_cuota', label: 'Modificación de cuota' },
    { value: 'pedidos', label: 'Pedidos' },
    { value: 'administracion', label: 'Administración' }
  ];

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.startDate) params.append('start_date', format(filters.startDate, 'yyyy-MM-dd'));
      if (filters.endDate) params.append('end_date', format(filters.endDate, 'yyyy-MM-dd'));
      params.append('limit', filters.limit.toString());

      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setError('Error al cargar los logs de auditoría');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const getActionColor = (action: string) => {
    switch (action) {
      case 'cobranzas':
        return 'success';
      case 'modificacion_cuota':
        return 'warning';
      case 'pedidos':
        return 'primary';
      case 'administracion':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getActionLabel = (action: string) => {
    const actionType = actionTypes.find(a => a.value === action);
    return actionType ? actionType.label : action;
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={(theme) => ({ 
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            fontWeight: 600,
            color: theme.palette.mode === 'dark'
              ? theme.palette.common.white
              : '#1E293B',
            mb: { xs: 2, sm: 4 }
          })}
        >
          Logs de Auditoría
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Acción</InputLabel>
                <Select
                  value={filters.action}
                  label="Acción"
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                >
                  <MenuItem value="">Todas las acciones</MenuItem>
                  {actionTypes.map((action) => (
                    <MenuItem key={action.value} value={action.value}>
                      {action.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Fecha Inicio"
                value={filters.startDate}
                onChange={(date) => setFilters({ ...filters, startDate: date })}
                slots={{
                  textField: TextField
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Fecha Fin"
                value={filters.endDate}
                onChange={(date) => setFilters({ ...filters, endDate: date })}
                slots={{
                  textField: TextField
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Límite</InputLabel>
                <Select
                  value={filters.limit}
                  label="Límite"
                  onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) })}
                >
                  <MenuItem value={50}>50 registros</MenuItem>
                  <MenuItem value={100}>100 registros</MenuItem>
                  <MenuItem value={200}>200 registros</MenuItem>
                  <MenuItem value={500}>500 registros</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {`Total de registros: ${logs.length}`}
          </Typography>
        </Box>

        {/* Tabla de logs */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Fecha/Hora</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Usuario</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Acción</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Descripción</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm')}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {log.user?.username || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Chip 
                        label={getActionLabel(log.action)} 
                        color={getActionColor(log.action) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {log.description}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {log.ip_address || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default AuditLogs;
